import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import confetti from 'canvas-confetti';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { MonthlyChart, YearlyChart } from './InterventionsCharts';
import { LeaderboardModal } from './LeaderboardPage';
import { Achievements } from './Achievements';
import { PageHeader } from '@/shared/PageHeader';

export function InterventionsPage(): React.JSX.Element {
  usePageTitle('Interventi');
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [category, setCategory] = useState('Soccorso');
  const [note, setNote] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['interventions', year],
    queryFn: async () => (await api.get('/interventions', { params: { year } })).data,
  });

  const addMut = useMutation({
    mutationFn: async () => {
      await api.post('/interventions', {
        date: new Date().toISOString(),
        category, note: note || null,
      });
    },
    onSuccess: async () => {
      setNote('');
      toast.show('Intervento registrato');
      // Confetti ogni 5 interventi totali
      const res = await api.get('/interventions', { params: { year } });
      const total = res.data?.total ?? 0;
      if (total > 0 && total % 5 === 0) {
        confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 } });
      }
      qc.invalidateQueries({ queryKey: ['interventions'] });
    },
    onError: () => toast.show('Errore durante il salvataggio', 'error'),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/interventions/${id}`); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['interventions'] });
      toast.show('Intervento eliminato');
    },
    onError: () => toast.show('Errore durante l\'eliminazione', 'error'),
  });

  async function handleDelete(id: string) {
    const ok = await confirm({
      title: 'Elimina intervento',
      message: 'Sei sicuro di voler eliminare questo intervento?',
      confirmLabel: 'Elimina',
      variant: 'danger',
    });
    if (ok) delMut.mutate(id);
  }

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="page-container">
      <PageHeader title="📋 Registro Interventi"
        actions={(
          <>
            <label style={{ fontSize: 14, color: '#666', fontWeight: 600 }}>Anno:</label>
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}
              style={{ width: 120, padding: '8px 12px', borderRadius: 8, border: '1px solid #ced4da' }}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </>
        )} />

      <div className="card">
        <h3 style={{ marginBottom: 15 }}>Inserimento rapido</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Categoria</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option>Soccorso</option>
              <option>Incendio</option>
              <option>Stradale</option>
              <option>Altro</option>
            </select>
          </div>
          <div className="form-group">
            <label>Nota (opzionale)</label>
            <input placeholder="Dettagli intervento" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <button onClick={() => addMut.mutate()} disabled={addMut.isPending} className="btn-success">
          🔥 Registra intervento
        </button>
      </div>

      {/* min(400px,100%): su schermi < 400px la colonna non forza i 400px fissi
          -> niente scroll orizzontale su smartphone (punto 8). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))', gap: 16, marginTop: 20 }}>
        <MonthlyChart year={year} />
        <YearlyChart />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 16 }}>
        <Achievements year={year} />
        <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 30 }}>
          <h3 style={{ marginTop: 0 }}>🏆 Classifica</h3>
          <button onClick={() => setShowLeaderboard(true)}
            style={{ padding: '12px 24px', border: 'none', borderRadius: 8, cursor: 'pointer',
                     background: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)', color: '#000',
                     fontWeight: 700, fontSize: 15 }}>
            Apri classifica
          </button>
        </div>
      </div>

      {showLeaderboard && <LeaderboardModal onClose={() => setShowLeaderboard(false)} />}

      <h3 style={{ marginTop: 30, marginBottom: 12 }}>Storico {year}</h3>
      {isLoading ? (
        <div className="empty-state">Caricamento...</div>
      ) : data?.items?.length ? (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Nota</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((i: { id: string; date: string; category?: string; note?: string }) => (
                <tr key={i.id}>
                  <td>{new Date(i.date).toLocaleDateString('it-IT')}</td>
                  <td>{i.note ?? '-'}</td>
                  <td>
                    <button className="btn-delete" onClick={() => handleDelete(i.id)}>
                      🗑 Elimina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <div style={{ fontSize: 48, marginBottom: 10 }}>📋</div>
          Nessun intervento registrato in questo anno
        </div>
      )}
    </div>
  );
}
